import * as React from 'react';
import styles from './icon-button.module.scss';
import { Icon } from '../icon/icon';
import { ButtonFocusHandler, ButtonFocusHandlerChildProps } from '../button-base/button-focus-handler';
import { ButtonBaseProps, ButtonIconProps, ButtonSizeProps, ButtonTypeProps } from '../button-base/button-interfaces';
import { AriaRoletypeInterface } from '../AccessibilityProps';
import { getDataAttributes } from '../../util/attribute-util';

export interface IconButtonBaseProps extends ButtonBaseProps, ButtonSizeProps, Required<ButtonIconProps> {
  /**
   * nontextual interface elements must have a screen reader friendly alternative representation
   */

  aria: AriaRoletypeInterface & Required<Pick<AriaRoletypeInterface, 'aria-label'>>;
}

export type IconButtonProps = ButtonTypeProps & IconButtonBaseProps;

const Content = React.forwardRef<any, IconButtonProps & ButtonFocusHandlerChildProps>((props, ref) => {
  const classes = [styles['component']];
  const attributes: React.HTMLAttributes<HTMLElement> = { id: props.id };

  props.size && classes.push(styles[props.size]);

  props.hasFocus && (attributes['data-focus'] = true);
  props.hasHover && (attributes['data-hover'] = true);

  const content: React.ReactNode = (
    <React.Fragment>{props.icon && <Icon size={props.size === 'large' ? 16 : 12} name={props.icon} />}</React.Fragment>
  );
  switch (props.type) {
    case 'button': {
      const buttonAttributes: React.HTMLAttributes<HTMLButtonElement> = { ...attributes };
      props.disabled && (buttonAttributes['disabled'] = true);
      return (
        <button
          ref={ref}
          {...buttonAttributes}
          type="button"
          onClick={props.onClick}
          className={classes.join(' ')}
          title={props.aria['aria-label']}
          {...props.aria}
        >
          {content}
        </button>
      );
    }
    case 'anchor': {
      const anchorAttributes: React.HTMLAttributes<HTMLAnchorElement> = { ...attributes };
      props.disabled && (anchorAttributes['data-disabled'] = true);
      return (
        <a
          ref={ref}
          target={props.target}
          {...anchorAttributes}
          className={classes.join(' ')}
          title={props.aria['aria-label']}
          {...props.aria}
          {...(props.disabled ? {} : { href: props.href })}
        >
          {content}
        </a>
      );
    }
    case 'cosmetic': {
      const divAttributes: React.HTMLAttributes<HTMLDivElement> = { ...attributes };
      props.disabled && (divAttributes['disabled'] = true);
      const cosmeticAria = { ...props.aria };
      delete cosmeticAria['aria-label'];
      return (
        <div
          ref={ref}
          role={props.role}
          {...divAttributes}
          onClick={props.onClick}
          className={classes.join(' ')}
          title={props.aria['aria-label']}
          {...cosmeticAria}
        >
          {content}
        </div>
      );
    }
  }
});

const componentName = 'IconButton';

export class IconButton extends React.Component<IconButtonProps, any> {
  static defaultProps: Partial<IconButtonProps> = {
    size: 'large',
    type: 'button',
    aria: { 'aria-label': '' },
  };

  render() {
    return (
      <ButtonFocusHandler
        initialFocus={this.props.hasFocus}
        render={({ hasFocus, hasHover, ref }) => <Content ref={ref} {...this.props} hasFocus={hasFocus} hasHover={hasHover} />}
        data-component={componentName}
        {...getDataAttributes(this.props)}
      />
    );
  }
}

IconButton['displayName'] = componentName;
